/**
 * BrightPath - BudgetsPage
 * BP-04-04: Expenses Frontend
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  PiggyBank,
  TrendingUp,
  Copy,
  AlertTriangle,
} from 'lucide-react';
import {
  useBudgets,
  useBudgetStatus,
  useCreateBudget,
  useUpdateBudget,
  useDeleteBudget,
  useCopyBudgets,
} from '../hooks';
import { useExpenseStore } from '../stores/expenseStore';
import {
  BudgetCard,
  BudgetForm,
  BudgetProgressBar,
} from '../components';
import type { Budget, BudgetCreate, BudgetUpdate, ExpenseCategory } from '../types';
import { getCurrentBudgetPeriod, formatBudgetPeriod } from '../types';
import { formatCurrency, cn } from '@/core/lib/utils';

export function BudgetsPage() {
  const familyId = 'current';
  const { budgetYear, budgetMonth, setBudgetPeriod } = useExpenseStore();
  const { year: currentYear, month: currentMonth } = getCurrentBudgetPeriod();

  const [showForm, setShowForm] = useState(false);
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null);

  const { data: budgets, isLoading } = useBudgets(familyId, budgetYear, budgetMonth);
  const { data: budgetStatuses } = useBudgetStatus(familyId, budgetYear, budgetMonth);
  const { mutateAsync: createBudget, isPending: isCreating } = useCreateBudget();
  const { mutateAsync: updateBudget, isPending: isUpdating } = useUpdateBudget();
  const { mutate: deleteBudget } = useDeleteBudget();
  const { mutate: copyBudgets, isPending: isCopying } = useCopyBudgets();

  const existingCategories = budgets?.map((b) => b.category) || [];

  const totalBudget = budgets?.reduce((sum, b) => sum + b.monthlyBudget, 0) || 0;
  const totalSpent = budgetStatuses?.reduce((sum, s) => sum + s.spent, 0) || 0;
  const overBudgetCount = budgetStatuses?.filter((s) => s.isOverBudget).length || 0;

  const handleSubmit = async (data: BudgetCreate | BudgetUpdate) => {
    if (editingBudget) {
      await updateBudget({ budgetId: editingBudget.id, data: data as BudgetUpdate });
    } else {
      await createBudget({ familyId, data: data as BudgetCreate });
    }
    setShowForm(false);
    setEditingBudget(null);
  };

  const handleEdit = (budget: Budget) => {
    setEditingBudget(budget);
    setShowForm(true);
  };

  const handleDelete = (budget: Budget) => {
    if (confirm(`Delete ${budget.category} budget?`)) {
      deleteBudget({ budgetId: budget.id });
    }
  };

  const handleCopyFromLastMonth = () => {
    const lastMonth = budgetMonth === 1 ? 12 : budgetMonth - 1;
    const lastYear = budgetMonth === 1 ? budgetYear - 1 : budgetYear;
    copyBudgets({
      familyId,
      sourceYear: lastYear,
      sourceMonth: lastMonth,
      targetYear: budgetYear,
      targetMonth: budgetMonth,
    });
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <div className="bg-white border-b border-warm-gray/10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/family/expenses"
                className="flex items-center gap-2 text-warm-gray hover:text-navy transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-display font-semibold text-navy">Monthly Budgets</h1>
                <p className="text-sm text-warm-gray">
                  {formatBudgetPeriod(budgetYear, budgetMonth)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={`${budgetYear}-${budgetMonth}`}
                onChange={(e) => {
                  const [year, month] = e.target.value.split('-').map(Number);
                  setBudgetPeriod(year, month);
                }}
                className="px-4 py-2 bg-cream rounded-lg"
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const date = new Date();
                  date.setMonth(date.getMonth() - i + 3);
                  return (
                    <option key={i} value={`${date.getFullYear()}-${date.getMonth() + 1}`}>
                      {date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </option>
                  );
                })}
              </select>

              {budgets?.length === 0 && (
                <button
                  onClick={handleCopyFromLastMonth}
                  disabled={isCopying}
                  className="flex items-center gap-2 px-4 py-2 text-navy hover:bg-cream rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Copy from Last Month
                </button>
              )}

              <button
                onClick={() => {
                  setEditingBudget(null);
                  setShowForm(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Budget
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-warm-gray/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-sage/10 flex items-center justify-center">
                <PiggyBank className="w-5 h-5 text-sage" />
              </div>
              <span className="text-warm-gray">Total Budget</span>
            </div>
            <p className="text-2xl font-bold text-navy">{formatCurrency(totalBudget)}</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-warm-gray/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-terracotta/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-terracotta" />
              </div>
              <span className="text-warm-gray">Total Spent</span>
            </div>
            <p className="text-2xl font-bold text-navy">{formatCurrency(totalSpent)}</p>
            <p className="text-sm text-warm-gray">
              {totalBudget > 0
                ? `${((totalSpent / totalBudget) * 100).toFixed(0)}% of budget`
                : 'No budget set'}
            </p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-warm-gray/10">
            <div className="flex items-center gap-3 mb-3">
              <div
                className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center',
                  overBudgetCount > 0 ? 'bg-red-100' : 'bg-green-100'
                )}
              >
                <AlertTriangle
                  className={cn('w-5 h-5', overBudgetCount > 0 ? 'text-red-500' : 'text-green-500')}
                />
              </div>
              <span className="text-warm-gray">Status</span>
            </div>
            <p className="text-2xl font-bold text-navy">
              {overBudgetCount > 0 ? `${overBudgetCount} Over Budget` : 'On Track'}
            </p>
          </div>
        </div>

        {/* Budget Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-navy/50"
              onClick={() => {
                setShowForm(false);
                setEditingBudget(null);
              }}
            />
            <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 p-6">
              <h2 className="text-xl font-semibold text-navy mb-6">
                {editingBudget ? 'Edit Budget' : 'Create Budget'}
              </h2>
              <BudgetForm
                budget={editingBudget || undefined}
                existingCategories={editingBudget ? [] : existingCategories as ExpenseCategory[]}
                onSubmit={handleSubmit}
                onCancel={() => {
                  setShowForm(false);
                  setEditingBudget(null);
                }}
                isSubmitting={isCreating || isUpdating}
              />
            </div>
          </div>
        )}

        {/* Budgets Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-5 animate-pulse">
                <div className="h-12 w-12 bg-cream-dark rounded-xl mb-4" />
                <div className="h-4 bg-cream-dark rounded w-1/2 mb-2" />
                <div className="h-8 bg-cream-dark rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : budgets && budgets.length > 0 ? (
          <>
            {/* Progress Bars */}
            {budgetStatuses && budgetStatuses.length > 0 && (
              <div className="mb-8 space-y-4">
                <h2 className="font-semibold text-navy">Budget Progress</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {budgetStatuses.map((status) => (
                    <BudgetProgressBar
                      key={status.budget.id}
                      budgetStatus={status}
                      onClick={() => handleEdit(status.budget)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Budget Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {budgets.map((budget) => {
                const status = budgetStatuses?.find((s) => s.budget.id === budget.id);
                return (
                  <BudgetCard
                    key={budget.id}
                    budget={budget}
                    status={status}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                );
              })}
            </div>
          </>
        ) : (
          <div className="bg-white rounded-xl border border-warm-gray/10 p-12 text-center">
            <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mx-auto mb-4">
              <PiggyBank className="w-8 h-8 text-warm-gray" />
            </div>
            <h3 className="text-lg font-semibold text-navy mb-2">No Budgets Yet</h3>
            <p className="text-warm-gray mb-6 max-w-sm mx-auto">
              Set up monthly budgets to track your spending by category and stay on top of expenses.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create First Budget
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default BudgetsPage;

